import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import LabelIcon from "@material-ui/icons/Label";
import ViewWeekIcon from "@material-ui/icons/ViewWeek";
import SwapVertIcon from "@material-ui/icons/SwapVert";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import listPageStyles from "../../styles/listPageStyles";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import KanbanOrderModal from "../../components/KanbanOrderModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import MobileRecordCard, { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";
import toastError from "../../errors/toastError";
import { Chip } from "@material-ui/core";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_TAGS") {
    const tags = action.payload;
    const newTags = [];

    tags.forEach((tag) => {
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
      } else {
        newTags.push(tag);
      }
    });

    return [...state, ...newTags];
  }

  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tag.id);

    if (tagIndex !== -1) {
      state[tagIndex] = tag;
      return [...state];
    } else {
      return [tag, ...state];
    }
  }

  if (action.type === "DELETE_TAG") {
    const tagId = action.payload;

    const tagIndex = state.findIndex((s) => s.id === tagId);
    if (tagIndex !== -1) {
      state.splice(tagIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  ...listPageStyles(theme),
  tagChip: {
    color: "#fff",
    fontWeight: 600,
    fontSize: 12,
    borderRadius: 12,
    height: 24,
  },
  kanbanChip: {
    fontWeight: 600,
    fontSize: 12,
    height: 24,
    borderRadius: 12,
    color: theme.palette.type === "dark" ? "#CE93D8" : "#7B1FA2",
    backgroundColor: "rgba(156, 39, 176, 0.14)",
    "& .MuiChip-icon": { color: "inherit", fontSize: 14 },
  },
  count: {
    fontVariantNumeric: "tabular-nums",
  },
  mobileList: {
    padding: theme.spacing(1),
  },
}));

const Tags = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [kanbanOrderModalOpen, setKanbanOrderModalOpen] = useState(false);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await api.get("/tags/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_TAGS", payload: data.tags });
      setHasMore(data.hasMore);
      if (typeof data.count === "number") setTotalCount(data.count);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, fetchTags]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });

    socket.on("user", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tags });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAG", payload: +data.tagId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [user]);

  // A reordenação das raias grava em todas as tags Kanban da empresa, por
  // isso o endpoint é restrito a admin — o botão acompanha essa regra.
  const isAdmin = user.profile === "admin";

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchTags();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderActions = (tag) => (
    <div className={classes.actions}>
      <Tooltip title="Editar">
        <IconButton size="small" className={classes.iconAction} onClick={() => handleEditTag(tag)} aria-label="Editar">
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton
          size="small"
          className={classes.deleteAction}
          aria-label="Excluir"
          onClick={() => {
            setConfirmModalOpen(true);
            setDeletingTag(tag);
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const renderKanban = (tag) =>
    tag.kanban === 1 ? (
      <Chip
        className={classes.kanbanChip}
        icon={<ViewWeekIcon />}
        label="Etapa do Kanban"
        size="small"
      />
    ) : null;

  const countLabel =
    totalCount === null
      ? ""
      : searchParam
      ? `${totalCount} ${totalCount === 1 ? "etiqueta encontrada" : "etiquetas encontradas"}`
      : `${totalCount} ${totalCount === 1 ? "etiqueta cadastrada" : "etiquetas cadastradas"}`;

  const emptyState = (
    <Box className={classes.emptyState}>
      <LabelIcon className={classes.emptyIcon} />
      <Typography variant="body2">
        {searchParam
          ? `Nenhuma etiqueta encontrada para "${searchParam}"`
          : "Nenhuma etiqueta cadastrada ainda."}
      </Typography>
      {!searchParam && (
        <Button
          variant="outlined"
          className={`${classes.headerButton} ${classes.secondaryButton}`}
          startIcon={<AddIcon />}
          onClick={handleOpenTagModal}
        >
          Criar primeira tag
        </Button>
      )}
    </Box>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingTag && `${i18n.t("tags.confirmationModal.deleteTitle")}`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <KanbanOrderModal
        open={kanbanOrderModalOpen}
        onClose={() => setKanbanOrderModalOpen(false)}
        reload={fetchTags}
      />
      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        reload={fetchTags}
        aria-labelledby="form-dialog-title"
        tagId={selectedTag && selectedTag.id}
      />

      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>{i18n.t("tags.title")}</Title>
          {countLabel && (
            <Typography variant="body2" className={classes.muted}>
              {countLabel}
            </Typography>
          )}
        </div>
        <div className={classes.headerButtons}>
          {isAdmin && (
            <Button
              variant="outlined"
              className={`${classes.headerButton} ${classes.secondaryButton}`}
              startIcon={<SwapVertIcon />}
              onClick={() => setKanbanOrderModalOpen(true)}
            >
              Ordenar Kanban
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.headerButton}
            startIcon={<AddIcon />}
            onClick={handleOpenTagModal}
          >
            Nova tag
          </Button>
        </div>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar etiqueta"
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            inputProps={{ "aria-label": "Buscar etiquetas" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={classes.tableWrapper} onScroll={handleScroll}>
          {!loading && tags.length === 0 && emptyState}

          {isMobile ? (
            (tags.length > 0 || loading) && (
              <div className={classes.mobileList}>
                {tags.map((tag) => (
                  <MobileRecordCard
                    key={tag.id}
                    title={
                      <Chip
                        className={classes.tagChip}
                        style={{ backgroundColor: tag.color }}
                        label={tag.name}
                        size="small"
                      />
                    }
                    headerAction={renderActions(tag)}
                    rows={[{ label: "Atendimentos", value: tag.ticketsCount }]}
                    chips={renderKanban(tag)}
                  />
                ))}
                {loading && [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}
              </div>
            )
          ) : (
            (tags.length > 0 || loading) && (
              <Table stickyHeader size="small" style={{ minWidth: 620 }}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>
                      Tag
                    </TableCell>
                    <TableCell className={classes.headCell}>Uso</TableCell>
                    <TableCell className={classes.headCell} align="right">
                      Atendimentos
                    </TableCell>
                    <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20, width: 110 }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tags.map((tag) => (
                    <TableRow key={tag.id} className={classes.row}>
                      <TableCell style={{ paddingLeft: 20 }}>
                        <Chip
                          className={classes.tagChip}
                          style={{ backgroundColor: tag.color }}
                          label={tag.name}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {renderKanban(tag) || (
                          <span className={classes.muted} style={{ fontSize: 13 }}>
                            Contatos e atendimentos
                          </span>
                        )}
                      </TableCell>
                      <TableCell align="right" className={classes.count}>
                        {tag.ticketsCount ?? 0}
                      </TableCell>
                      <TableCell style={{ paddingRight: 20 }}>{renderActions(tag)}</TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={4} />}
                </TableBody>
              </Table>
            )
          )}
        </div>

        {tags.length > 0 && (
          <div className={classes.footer}>
            {`Mostrando ${tags.length}${totalCount !== null ? ` de ${totalCount}` : ""}`}
            {!loading && !hasMore ? " · todas carregadas" : ""}
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Tags;
